import { Button } from "@gigpro/ui/Button";
import { Dialog, DialogFooter } from "@gigpro/ui/Dialog";
import { Form } from "@gigpro/ui/Form";
import { Textfield } from "@gigpro/ui/Form/Textfield";
import { removeEmptyFields, useZodForm } from "@gigpro/ui/Form/utils";
import { ApiError } from "@sidegigllc/api";
import {
  useStafferGigsTemplatesCreate,
  useStafferGigsTemplatesPartialUpdate,
  type ForManagerLocationDetail,
} from "@sidegigllc/api/client";
import { useGigTemplates } from "api/queries/gigPostTemplates";
import { type GigTemplatePayload, gigTemplatePayloadSchema } from "api/resources/templates";
import type { Pro } from "domains/postGig/providers/PostGigData/types";
import React from "react";
import type { ModalBaseProps } from "types/utils";
import * as z from "zod/mini";

interface SaveTemplateDialogProps extends ModalBaseProps<"new"> {
  location: ForManagerLocationDetail;
  template: Pro;
  onSuccess: (values: Pro) => void;
}

const formId = "template-name-form";
const formSchema = z.pick(gigTemplatePayloadSchema, { name: true });
type FormValues = z.infer<typeof formSchema>;

export function SaveTemplateDialog({
  open,
  onOpenChange,
  onSuccess,
  location,
  template,
}: Readonly<SaveTemplateDialogProps>) {
  const { invalidate: invalidateTemplates } = useGigTemplates({ location_id: location.id });

  const { mutateAsync: createTemplate } = useStafferGigsTemplatesCreate();
  const { mutateAsync: updateTemplate } = useStafferGigsTemplatesPartialUpdate();

  const form = useZodForm({ schema: formSchema });

  React.useEffect(() => {
    if (open) return;
    form.reset({ name: template.name ?? `${template.skill_name} Template` });
  }, [template, form, open]);

  const onSubmit = async ({ name }: FormValues) => {
    const { requirements, ...payload } = removeEmptyFields({ ...template, name });
    const data = {
      ...payload,
      location_id: location.id,
      auto_select_enabled: payload.auto_select_enabled ?? undefined,
      requirement_ids: requirements?.map((r) => r.id) ?? [],
    };
    try {
      const { id } = template.id ? await updateTemplate({ id: template.id, data }) : await createTemplate({ data });
      void invalidateTemplates();
      onOpenChange(false);
      // Write response is item_ids-only; re-attach the form's block so the posted gig keeps it.
      onSuccess({ ...template, name, id, requirements, uniform: template.uniform });
    } catch (error) {
      const errors = ApiError.fromThrown<GigTemplatePayload>(error);
      errors.setFormErrors(form.setError);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange} className="max-w-sm">
      <Form id={formId} form={form} onSubmit={onSubmit}>
        <Textfield
          control={form.control}
          name="name"
          label="Give a name for this template"
          placeholder="Line Cook Template 1"
        />
      </Form>
      <DialogFooter>
        <Button size="small" onClick={() => onOpenChange(false)} disabled={form.formState.isSubmitting}>
          Cancel
        </Button>
        <Button size="small" variant="contained" type="submit" form={formId} loading={form.formState.isSubmitting}>
          Save
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
